import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Bar,
  BarChart,
  Legend,
} from 'recharts';
import { getApi, downloadApi } from '../../Services/apiService';
import {
  AdminStatCard,
  AdminSearch,
  AdminBadge,
  statusToBadge,
  AdminModal,
  AdminModalFooter,
  AdminPagination,
} from '../components/AdminUi';
import Button from '../../Components/Button/Button';

const defaultRevenue = {
  platform_revenue: 0,
  platform_revenue_change_percent: 0,
  recorded_payments: 0,
  recorded_payments_count: 0,
  pending: 0,
  pending_count: 0,
  screening_fees: 0,
  revenue_trend: [],
  revenue_by_type: [],
};

const defaultPagination = {
  currentPage: 1,
  pageSize: 10,
  totalPages: 1,
  totalItems: 0,
};

function normalizePagination(pag, fallbackPageSize) {
  const pageSize = pag?.pageSize ?? fallbackPageSize ?? 10;
  const totalItems = pag?.totalItems ?? 0;
  const totalPages = pag?.totalPages ?? Math.max(1, Math.ceil(totalItems / pageSize));
  return {
    currentPage: pag?.currentPage ?? 1,
    pageSize,
    totalPages,
    totalItems,
  };
}

const formatAmount = (amount) => {
  const num = Number(amount);
  if (!Number.isFinite(num)) return '$0';
  const prefix = num < 0 ? '-' : '+';
  return `${prefix}$${Math.abs(num).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
};

const formatCurrency = (amount) => {
  const num = Number(amount);
  if (!Number.isFinite(num)) return '$0';
  return `$${num.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
};

const formatDate = (dateStr) => {
  if (dateStr == null || dateStr === '') return '—';
  const date =
    typeof dateStr === 'number' || /^\d+$/.test(String(dateStr))
      ? new Date(Number(dateStr))
      : new Date(`${dateStr}T12:00:00`);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

function formatChartYAxis(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return '$0';
  if (num >= 1000) return `$${(num / 1000).toFixed(num % 1000 === 0 ? 0 : 1)}k`;
  if (Number.isInteger(num)) return `$${num}`;
  return `$${num.toFixed(2)}`;
}

function normalizeRole(role) {
  const value = String(role ?? '').toLowerCase();
  if (value === '1' || value === 'landlord') return 'landlord';
  if (value === '2' || value === 'tenant') return 'tenant';
  return value;
}

function formatTransactionType(type) {
  if (!type) return '';
  return String(type)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatChangePercent(percent) {
  const num = Number(percent);
  if (!Number.isFinite(num)) return '0% vs last period';
  const sign = num > 0 ? '+' : '';
  return `${sign}${num}% vs last period`;
}

function normalizeTransaction(tx, index) {
  const rawDate = tx.date ?? tx.created_at ?? tx.createdAt ?? tx.payment_date;
  let date = rawDate;
  if (typeof rawDate === 'number' || /^\d+$/.test(String(rawDate ?? ''))) {
    date = new Date(Number(rawDate)).toISOString().slice(0, 10);
  }

  const rawType = tx.type ?? '';
  const rawRole = tx.role ?? tx.user_role ?? '';

  return {
    id: tx.id ?? `tx-${index}`,
    transactionId: tx.transaction_id ?? tx.transactionId ?? tx.reference ?? '—',
    date,
    user: tx.name ?? tx.user ?? tx.user_name ?? '—',
    email: tx.email ?? '',
    userRole: normalizeRole(rawRole),
    type: formatTransactionType(rawType),
    rawType: String(rawType).toLowerCase(),
    description: tx.description ?? '',
    paymentMethod: tx.payment_method ?? tx.paymentMethod ?? '—',
    amount: Number(tx.amount ?? 0),
    status: String(tx.status ?? '').toLowerCase(),
    reference: tx.reference ?? '',
  };
}

export default function RevenuePage() {
  const [revenue, setRevenue] = useState(defaultRevenue);
  const [transactions, setTransactions] = useState([]);
  const [pagination, setPagination] = useState(defaultPagination);
  const [isLoading, setIsLoading] = useState(true);
  const [isTransactionsLoading, setIsTransactionsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [selectedTx, setSelectedTx] = useState(null);
  const [isDetailLoading, setIsDetailLoading] = useState(false);

  const searchTimeoutRef = useRef(null);
  const previousDebouncedSearchRef = useRef('');

  useEffect(() => {
    let cancelled = false;

    const fetchRevenue = async () => {
      setIsLoading(true);
      try {
        const response = await getApi('/admin/revenue');
        if (cancelled) return;

        if (response?.statusCode === 200 && response?.data) {
          const data = response.data;
          setRevenue({
            ...defaultRevenue,
            ...data,
            revenue_trend: data.revenue_trend ?? [],
            revenue_by_type: data.revenue_by_type ?? [],
          });
        } else {
          toast.error(response?.message || 'Failed to load revenue details');
        }
      } catch (error) {
        if (!cancelled) {
          const message =
            error?.response?.data?.message || error?.message || 'Failed to load revenue details';
          toast.error(message);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchRevenue();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  useEffect(() => {
    let cancelled = false;

    const fetchTransactions = async () => {
      setIsTransactionsLoading(true);
      try {
        const params = {
          page: pagination.currentPage,
          limit: pagination.pageSize,
        };

        if (debouncedSearch.trim()) {
          params.search = debouncedSearch.trim();
        }
        if (roleFilter !== 'all') {
          params.role = roleFilter;
        }
        if (typeFilter !== 'all') {
          params.type = typeFilter;
        }
        if (statusFilter !== 'all') {
          params.status = statusFilter;
        }

        const response = await getApi('/admin/transactions', { params });
        if (cancelled) return;

        if (response?.statusCode === 200 && response?.data) {
          const list = response.data.transactions ?? response.data.items ?? [];
          setTransactions(list.map(normalizeTransaction));
          setPagination((prev) =>
            normalizePagination(response.data.pagination, prev.pageSize)
          );
        } else {
          toast.error(response?.message || 'Failed to load transactions');
          setTransactions([]);
        }
      } catch (error) {
        if (!cancelled) {
          const message =
            error?.response?.data?.message || error?.message || 'Failed to load transactions';
          toast.error(message);
          setTransactions([]);
        }
      } finally {
        if (!cancelled) {
          setIsTransactionsLoading(false);
        }
      }
    };

    fetchTransactions();
    return () => {
      cancelled = true;
    };
  }, [
    pagination.currentPage,
    pagination.pageSize,
    debouncedSearch,
    roleFilter,
    typeFilter,
    statusFilter,
  ]);

  useEffect(() => {
    if (debouncedSearch !== previousDebouncedSearchRef.current && pagination.currentPage !== 1) {
      previousDebouncedSearchRef.current = debouncedSearch;
      setPagination((prev) => ({ ...prev, currentPage: 1 }));
      return;
    }
    previousDebouncedSearchRef.current = debouncedSearch;
  }, [debouncedSearch, pagination.currentPage]);

  const handleFilterChange = (setter) => (event) => {
    setter(event.target.value);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const closeModal = () => {
    setSelectedTx(null);
    setIsDetailLoading(false);
  };

  const openView = async (transaction) => {
    if (!transaction?.id) {
      toast.error('Transaction ID is missing');
      return;
    }

    setSelectedTx(null);
    setIsDetailLoading(true);

    try {
      const response = await getApi(`/admin/transactions/${transaction.id}`);
      if (response?.statusCode === 200 && response?.data) {
        setSelectedTx(normalizeTransaction(response.data, 0));
      } else {
        toast.error(response?.message || 'Failed to load transaction details');
      }
    } catch (error) {
      const message =
        error?.response?.data?.message || error?.message || 'Failed to load transaction details';
      toast.error(message);
    } finally {
      setIsDetailLoading(false);
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await downloadApi('/admin/revenue/export', 'revenue-report.xlsx');
      toast.success('Revenue report exported successfully');
    } catch (error) {
      const message =
        error?.response?.data?.message || error?.message || 'Failed to export revenue report';
      toast.error(message);
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="admin-page">
        <p style={{ margin: 0, color: '#7c7e93' }}>Loading revenue details...</p>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-toolbar">
        <span />
        <Button variant="secondary" onClick={handleExport} disabled={isExporting}>
          {isExporting ? 'Exporting...' : 'Export Report'}
        </Button>
      </div>

      <div className="dashboard-stats admin-revenue-stats">
        <AdminStatCard
          label="Platform Revenue"
          value={formatCurrency(revenue.platform_revenue)}
          icon="/assets/images/Stripe.svg"
          subtitle={formatChangePercent(revenue.platform_revenue_change_percent)}
        />
        <AdminStatCard
          label="Recorded Payments"
          value={formatCurrency(revenue.recorded_payments)}
          icon="/assets/images/sidebardashboardIcon.svg"
          subtitle={`${revenue.recorded_payments_count} completed`}
        />
        <AdminStatCard
          label="Pending"
          value={formatCurrency(revenue.pending)}
          icon="/assets/images/SearchIcon.svg"
          subtitle={`${revenue.pending_count} awaiting payment`}
        />
        <AdminStatCard
          label="Screening Fees"
          value={formatCurrency(revenue.screening_fees)}
          icon="/assets/images/UsersTenantDetail.svg"
          subtitle="Tenant screening revenue"
        />
      </div>

      <div className="admin-chart-grid">
        <div className="dashboard-section admin-chart-card">
          <div className="dashboard-section-header">
            <h2 className="dashboard-section-title">Revenue Trend</h2>
          </div>
          <div className="admin-chart-area">
            {revenue.revenue_trend.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={revenue.revenue_trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e8" />
                  <XAxis dataKey="month" />
                  <YAxis tickFormatter={formatChartYAxis} />
                  <Tooltip formatter={(v) => [formatCurrency(v), 'Revenue']} />
                  <Area type="monotone" dataKey="revenue" stroke="#1a5d3a" fill="#1a5d3a33" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <p style={{ margin: 0, color: '#7c7e93', padding: '24px 0' }}>No revenue trend data</p>
            )}
          </div>
        </div>
        <div className="dashboard-section admin-chart-card">
          <div className="dashboard-section-header">
            <h2 className="dashboard-section-title">Revenue by Type</h2>
          </div>
          <div className="admin-chart-area">
            {revenue.revenue_by_type.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={revenue.revenue_by_type}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e8" />
                  <XAxis dataKey="month" />
                  <YAxis tickFormatter={formatChartYAxis} />
                  <Tooltip formatter={(v) => formatCurrency(v)} />
                  <Legend />
                  <Bar dataKey="subscriptions" name="Subscriptions" fill="#1a5d3a" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="screening" name="Screening" fill="#e6a817" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p style={{ margin: 0, color: '#7c7e93', padding: '24px 0' }}>No revenue breakdown data</p>
            )}
          </div>
        </div>
      </div>

      <div className="dashboard-section admin-transaction-section">
        <div className="dashboard-section-header">
          <div>
            <h2 className="dashboard-section-title">Transaction History</h2>
            <p className="admin-section-subtitle">
              Platform fee and screening payments
            </p>
          </div>
          <span className="admin-transaction-count">
            {pagination.totalItems} records
          </span>
        </div>

        <div className="admin-toolbar admin-toolbar--nested">
          <AdminSearch
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search by user, email, reference, or description..."
            fullWidth
          />
          <div className="admin-toolbar-filters">
            <select className="admin-select" value={roleFilter} onChange={handleFilterChange(setRoleFilter)}>
              <option value="all">All Users</option>
              <option value="landlord">Landlords</option>
              <option value="tenant">Tenants</option>
            </select>
            <select className="admin-select" value={typeFilter} onChange={handleFilterChange(setTypeFilter)}>
              <option value="all">All Types</option>
              <option value="platform_fee">Platform Fee</option>
              <option value="screening">Screening</option>
            </select>
            <select className="admin-select" value={statusFilter} onChange={handleFilterChange(setStatusFilter)}>
              <option value="all">All Status</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
              <option value="refunded">Refunded</option>
            </select>
          </div>
        </div>

        <div className="admin-table-wrap">
          <div className="admin-table-scroll">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Transaction ID</th>
                <th>Date</th>
                <th>User</th>
                <th>Type</th>
                <th>Description</th>
                <th>Payment</th>
                <th>Amount</th>
                <th>Status</th>
                <th className="admin-table-actions">Details</th>
              </tr>
            </thead>
            <tbody>
              {isTransactionsLoading ? (
                <tr>
                  <td colSpan={9} className="admin-table-empty">
                    Loading transactions...
                  </td>
                </tr>
              ) : transactions.length === 0 ? (
                <tr>
                  <td colSpan={9} className="admin-table-empty">
                    No transactions match your filters.
                  </td>
                </tr>
              ) : (
                transactions.map((t) => (
                  <tr key={t.id}>
                    <td style={{ fontFamily: 'monospace', fontSize: 13, color: '#7c7e93' }}>
                      {t.transactionId}
                    </td>
                    <td>{formatDate(t.date)}</td>
                    <td>
                      <div className="admin-transaction-user">
                        <span style={{ fontWeight: 600 }}>{t.user}</span>
                        <span className="admin-transaction-user-meta">
                          {t.userRole === 'landlord' ? 'Landlord' : t.userRole === 'tenant' ? 'Tenant' : 'User'}
                          {t.email ? ` · ${t.email}` : ''}
                        </span>
                      </div>
                    </td>
                    <td>
                      <AdminBadge variant={statusToBadge(t.type)}>{t.type}</AdminBadge>
                    </td>
                    <td style={{ color: '#7c7e93', maxWidth: 220 }}>{t.description}</td>
                    <td>{t.paymentMethod}</td>
                    <td style={{ fontWeight: 600, color: t.amount < 0 ? '#c62828' : '#2c2c2c' }}>
                      {formatAmount(t.amount)}
                    </td>
                    <td>
                      <AdminBadge variant={statusToBadge(t.status)}>{t.status}</AdminBadge>
                    </td>
                    <td className="admin-table-actions">
                      <Button size="small" variant="outline" className="admin-table-btn" onClick={() => openView(t)}>
                        View
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </div>
        </div>

        <AdminPagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          totalItems={pagination.totalItems}
          pageSize={pagination.pageSize}
          onPageChange={(page) => setPagination((prev) => ({ ...prev, currentPage: page }))}
        />
      </div>

      <AdminModal
        open={isDetailLoading || !!selectedTx}
        onClose={closeModal}
        title="Payment Details"
        footer={
          !isDetailLoading && selectedTx ? (
            <AdminModalFooter onCancel={closeModal} onConfirm={closeModal} confirmLabel="Close" />
          ) : null
        }
      >
        {isDetailLoading && (
          <p style={{ margin: 0, color: '#7c7e93' }}>Loading transaction details...</p>
        )}
        {!isDetailLoading && selectedTx && (
          <div className="admin-detail-grid">
            <div>
              <p className="admin-detail-label">Transaction ID</p>
              <p className="admin-detail-value">{selectedTx.transactionId}</p>
            </div>
            <div>
              <p className="admin-detail-label">Reference</p>
              <p className="admin-detail-value">{selectedTx.reference || '—'}</p>
            </div>
            <div>
              <p className="admin-detail-label">Date</p>
              <p className="admin-detail-value">{formatDate(selectedTx.date)}</p>
            </div>
            <div>
              <p className="admin-detail-label">Status</p>
              <AdminBadge variant={statusToBadge(selectedTx.status)}>{selectedTx.status}</AdminBadge>
            </div>
            <div>
              <p className="admin-detail-label">User</p>
              <p className="admin-detail-value">{selectedTx.user}</p>
            </div>
            <div>
              <p className="admin-detail-label">Role</p>
              <p className="admin-detail-value" style={{ textTransform: 'capitalize' }}>
                {selectedTx.userRole || '—'}
              </p>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <p className="admin-detail-label">Email</p>
              <p className="admin-detail-value">{selectedTx.email || '—'}</p>
            </div>
            <div>
              <p className="admin-detail-label">Type</p>
              <AdminBadge variant={statusToBadge(selectedTx.type)}>{selectedTx.type}</AdminBadge>
            </div>
            <div>
              <p className="admin-detail-label">Payment method</p>
              <p className="admin-detail-value">{selectedTx.paymentMethod}</p>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <p className="admin-detail-label">Description</p>
              <p className="admin-detail-value">{selectedTx.description || '—'}</p>
            </div>
            <div>
              <p className="admin-detail-label">Amount</p>
              <p className="admin-detail-value" style={{ fontWeight: 700, color: selectedTx.amount < 0 ? '#c62828' : '#1a5d3a' }}>
                {formatAmount(selectedTx.amount)}
              </p>
            </div>
          </div>
        )}
      </AdminModal>
    </div>
  );
}
